import React from 'react';
import { AlertTriangle } from 'lucide-react';
import type { CompanyConflict } from '../../../types/kyb.types';

interface CompanyConflictWarningProps {
  conflict: CompanyConflict;
}

export const CompanyConflictWarning: React.FC<CompanyConflictWarningProps> = ({ conflict }) => {
  if (!conflict) return null;

  const severityStyles: Record<string, { bg: string; text: string }> = {
    CRITICAL: { bg: 'from-amber-500 to-orange-600', text: 'text-white' },      // Less dramatic than red
    HIGH: { bg: 'from-yellow-500 to-amber-600', text: 'text-gray-900' },       // Softer warm tone
    MEDIUM: { bg: 'from-blue-400 to-indigo-500', text: 'text-white' },         // Informational blue
    LOW: { bg: 'from-green-400 to-emerald-500', text: 'text-white' }           // Calm green
  };

  // Fallback to MEDIUM if severity is not recognized
  const styles = severityStyles[conflict.severity] || severityStyles.MEDIUM;

  return (
    <div className={`bg-gradient-to-r ${styles.bg} rounded-xl p-6 shadow-2xl opacity-95 backdrop-blur-sm`}>
      <div className="flex items-start gap-4">
        <AlertTriangle className={`w-8 h-8 flex-shrink-0 ${styles.text}`} />
        <div className="flex-1">
          <h3 className={`font-bold text-xl mb-2 ${styles.text}`}>Entity Verification Required</h3>
          <p className={`mb-3 ${styles.text}`}>{conflict.warning_message}</p>
          {conflict.geographical_context && (
            <div className="mb-3 p-3 bg-white/90 rounded-lg backdrop-blur-sm">
              <p className="text-sm font-semibold mb-1 text-gray-900">Entities Found:</p>
              <p className="text-sm text-gray-800 whitespace-pre-line">{conflict.geographical_context}</p>
            </div>
          )}
          <p className={`font-semibold ${styles.text}`}>{conflict.recommendation}</p>
        </div>
      </div>
    </div>
  );
};
